/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import type { StyleProps } from './types.js'
import { cx, mergeStyle, pickStyleProps } from './types.js'

export interface DatePickerProps extends StyleProps {
  /** Initial selected date in YYYY-MM-DD format. */
  value?: string
  /** Hidden input name for form submission. */
  name?: string
  /** Start week on Monday instead of Sunday. Default: false */
  firstDayOfWeek?: 0 | 1
  class?: string
  style?: string | Record<string, string>
  [key: string]: unknown
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

type CalendarCell = { day: number; month: number; year: number; outside: boolean }

function buildCalendar(year: number, month: number, startSunday: boolean): CalendarCell[] {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const prevMonthDays = new Date(year, month, 0).getDate()
  const offset = startSunday ? firstDay : (firstDay === 0 ? 6 : firstDay - 1)

  const cells: CalendarCell[] = []

  const prevM = month === 0 ? 11 : month - 1
  const prevY = month === 0 ? year - 1 : year
  for (let i = offset - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, month: prevM, year: prevY, outside: true })
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, outside: false })
  }

  const nextM = month === 11 ? 0 : month + 1
  const nextY = month === 11 ? year + 1 : year
  let nextDay = 1
  while (cells.length % 7 !== 0) {
    cells.push({ day: nextDay++, month: nextM, year: nextY, outside: true })
  }

  return cells
}

function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parseIso(v?: string): Date | null {
  if (!v) return null
  const d = new Date(`${v}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

function DatePicker({
  value: initialValue,
  name,
  firstDayOfWeek = 0,
  class: className,
  style,
  ...rest
}: DatePickerProps) {
  const today = new Date()
  const initial = parseIso(initialValue)

  const [viewYear, setViewYear] = createSignal(initial ? initial.getFullYear() : today.getFullYear())
  const [viewMonth, setViewMonth] = createSignal(initial ? initial.getMonth() : today.getMonth())
  const [selected, setSelected] = createSignal(initialValue ?? '')

  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const startSunday = firstDayOfWeek === 0
  const weekdays = startSunday
    ? ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
    : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

  const todayIso = toIso(today.getFullYear(), today.getMonth(), today.getDate())

  let rootEl: HTMLElement | null = null

  function prevMonth() {
    if (viewMonth() === 0) { setViewYear(viewYear() - 1); setViewMonth(11) }
    else setViewMonth(viewMonth() - 1)
  }

  function nextMonth() {
    if (viewMonth() === 11) { setViewYear(viewYear() + 1); setViewMonth(0) }
    else setViewMonth(viewMonth() + 1)
  }

  function selectDay(cell: CalendarCell) {
    if (cell.outside) {
      setViewYear(cell.year)
      setViewMonth(cell.month)
    }
    const dateStr = toIso(cell.year, cell.month, cell.day)
    setSelected(dateStr)
    if (rootEl) {
      rootEl.dispatchEvent(
        new CustomEvent('dv-date-change', { detail: { date: dateStr }, bubbles: true }),
      )
    }
  }

  return (
    <div
      class={cx('dv-date-picker', className)}
      style={mergeStyle(styleObj, style)}
      ref={(el: Element) => { rootEl = el as HTMLElement }}
      {...ownRest}
    >
      {name && <input type='hidden' name={name} value={() => selected()} />}
      <div class='dv-date-calendar'>
        <div class='dv-date-calendar-header'>
          <button
            type='button'
            class='dv-date-calendar-nav'
            onClick={prevMonth}
            aria-label='Previous month'
          >
            ‹
          </button>
          <span class='dv-date-calendar-title'>
            {() => `${MONTH_NAMES[viewMonth()]} ${viewYear()}`}
          </span>
          <button
            type='button'
            class='dv-date-calendar-nav'
            onClick={nextMonth}
            aria-label='Next month'
          >
            ›
          </button>
        </div>
        <div class='dv-date-calendar-weekdays'>
          {weekdays.map((wd) => (
            <div class='dv-date-calendar-weekday'>{wd}</div>
          ))}
        </div>
        <div class='dv-date-calendar-grid'>
          {() => {
            const year = viewYear()
            const month = viewMonth()
            const sel = selected()
            const cells = buildCalendar(year, month, startSunday)
            return cells.map((cell) => {
              const iso = toIso(cell.year, cell.month, cell.day)
              return (
                <button
                  type='button'
                  class='dv-date-calendar-cell'
                  data-today={iso === todayIso || undefined}
                  data-selected={iso === sel || undefined}
                  data-outside-month={cell.outside || undefined}
                  onClick={() => selectDay(cell)}
                  aria-label={iso}
                  aria-pressed={iso === sel}
                >
                  {cell.day}
                </button>
              )
            })
          }}
        </div>
      </div>
    </div>
  )
}

export default reactive(DatePicker)
